import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Menu,
  Stack,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { filterMenuSlotProps } from "./filterMenuStyles.js";
import { useThemeContext } from "../context/themeContext.js";

const pad = (n) => String(n).padStart(2, "0");
const toMonth = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;

/** Common periods, as { label, from, to } month ranges ("YYYY-MM") */
const getQuickPicks = () => {
  const now = new Date();
  const year = now.getFullYear();
  const lastMonth = toMonth(new Date(year, now.getMonth() - 1, 1));
  return [
    { label: "This month", from: toMonth(now), to: toMonth(now) },
    { label: "Last month", from: lastMonth, to: lastMonth },
    { label: "This year", from: `${year}-01`, to: `${year}-12` },
    { label: "Last year", from: `${year - 1}-01`, to: `${year - 1}-12` },
  ];
};

/**
 * Read a finish-date filter's URL token (see _apply_finish_date_filter in
 * filters.py) as { none, from, to } in months. A year widens to its first
 * and last month; a day (only reachable by editing the URL) narrows to its
 * month.
 */
const parseFinishDateFilter = (token) => {
  if (!token) return { none: false, from: "", to: "" };
  if (token === "none") return { none: true, from: "", to: "" };
  const asMonth = (part, isEnd) => {
    if (!part) return "";
    if (part.length === 4) return `${part}-${isEnd ? "12" : "01"}`;
    return part.slice(0, 7);
  };
  const [from, to] = token.includes("..") ? token.split("..") : [token, token];
  return { none: false, from: asMonth(from, false), to: asMonth(to, true) };
};

/** The shortest token for the draft: "2025-03", "2025" or "2025-01..2025-06" */
const serialiseFinishDateFilter = ({ none, from, to }) => {
  if (none) return "none";
  if (from && from === to) return from;
  const isWholeYear =
    from.endsWith("-01") &&
    to.endsWith("-12") &&
    from.slice(0, 4) === to.slice(0, 4);
  if (isWholeYear) return from.slice(0, 4);
  return `${from}..${to}`;
};

/**
 * Track's finish-date filter: a quick pick, a custom month range (either end
 * optional, for "since" / "until"), or "No finish date" instead. Nothing
 * applies until Apply.
 *
 * @param {string|null} value - The filter's current URL token, if set
 * @param {(token: string) => void} onApply
 * @param {() => void} onClear
 */
const FinishDateFilterMenu = ({
  value,
  onApply,
  onClear,
  anchorEl,
  onClose,
}) => {
  // Mounted fresh each time the menu opens, so this starts from the URL
  const [draft, setDraft] = useState(() => parseFinishDateFilter(value));
  // Touch devices get the browser's month input (the phone's own picker);
  // mouse users get MUI's, to match the rest of the app
  const { isTouchDevice } = useThemeContext();

  // "YYYY-MM" strings compare correctly as text
  const isBackwards = draft.from && draft.to && draft.from > draft.to;
  const canApply = draft.none || ((draft.from || draft.to) && !isBackwards);

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      slotProps={filterMenuSlotProps}
      // MUI's picker opens in its own popover outside the menu, which the
      // menu would otherwise keep pulling focus back from
      disableEnforceFocus={!isTouchDevice}
    >
      {/* One Stack, so every section sits the same distance apart */}
      <Stack spacing={2} sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {getQuickPicks().map((pick) => {
            const selected =
              !draft.none && draft.from === pick.from && draft.to === pick.to;
            return (
              <Chip
                key={pick.label}
                label={pick.label}
                size="small"
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                disabled={draft.none}
                onClick={() =>
                  setDraft({ none: false, from: pick.from, to: pick.to })
                }
              />
            );
          })}
        </Stack>

        <Stack direction="row" spacing={1}>
          {[
            ["from", "From"],
            ["to", "To"],
          ].map(([key, label]) =>
            isTouchDevice ? (
              <TextField
                key={key}
                type="month"
                label={label}
                size="small"
                value={draft[key]}
                disabled={draft.none}
                onChange={(event) =>
                  setDraft({ ...draft, [key]: event.target.value })
                }
                error={!!isBackwards}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            ) : (
              <LocalizationProvider
                key={key}
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
                // No "MMM YYYY" format hint in an empty field, focused or not
                localeText={{
                  fieldMonthPlaceholder: () => "",
                  fieldYearPlaceholder: () => "",
                }}
              >
                <DesktopDatePicker
                  label={label}
                  views={["year", "month"]}
                  openTo="month"
                  format="MMM YYYY"
                  value={draft[key] ? dayjs(`${draft[key]}-01`) : null}
                  disabled={draft.none}
                  // Cleared or half-typed dates leave that end open
                  onChange={(date) =>
                    setDraft({
                      ...draft,
                      [key]: date?.isValid() ? date.format("YYYY-MM") : "",
                    })
                  }
                  slotProps={{
                    // Kept compact to fit two side by side: the label sits
                    // in the empty field, a small calendar button, and no
                    // clear button (select the text and delete to clear an end)
                    textField: {
                      size: "small",
                      error: !!isBackwards,
                      sx: { flex: 1 },
                    },
                    openPickerButton: { size: "small" },
                    openPickerIcon: { fontSize: "small" },
                  }}
                />
              </LocalizationProvider>
            ),
          )}
        </Stack>

        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Checkbox
              sx={{ p: 0, mr: 1 }}
              checked={draft.none}
              onChange={(event) =>
                setDraft({ ...draft, none: event.target.checked })
              }
            />
          }
          label="No finish date"
        />

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" size="small" onClick={onClear}>
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!canApply}
            onClick={() => onApply(serialiseFinishDateFilter(draft))}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Menu>
  );
};

export default FinishDateFilterMenu;
